<!-- "Metrics" tab of the video dialog (inside #videoMetrics, VideoDialog.vue), drawn again from the start every time it
     is opened; shown like the "Released" tab (a published video or one in Ready). One card per platform the video came
     out on (its links in the "Released" tab, or metrics already typed), plus "Add a platform": the verdict (Bad /
     Average / Good: a tap on the chosen one clears it), the numbers copied from the platform (views, likes, comments,
     shares, saves, new followers, retention %), the average watch time, notes, and the day they were last changed
     (set to today at every change). The draft is the video store's `metrics`, saved with "Save video". Typed by hand:
     Hideo reads nothing from the platforms. Logic in src/features/metrics.ts. -->
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from '../../composables/useI18n'
import { useVideoStore } from '../../stores/video'
import { METRIC_NUMBERS, VERDICTS, metricPlatforms } from '../../features/metrics'
import { PUB_SOCIALS, pubUrl } from '../../features/published'
import { dayOf, isoDay } from '../../utils/date'
import { fmtDate } from '../../i18n'
import type { PlatformMetrics, PubLinkKey } from '../../data/types'

const { t } = useI18n()
const video = useVideoStore()
const M = video.metrics
const added = ref<string[]>([])
const links = computed(() => video.pub.links)
const platforms = computed(() => metricPlatforms({ pubLinks: Object.fromEntries(Object.entries(links.value).map(([k, v]) => [k, pubUrl(v)])), metrics: M }, added.value))
const others = computed(() => PUB_SOCIALS.filter(s => !platforms.value.includes(s[0])))
const social = (k: PubLinkKey) => PUB_SOCIALS.find(s => s[0] === k)!
const row = (k: PubLinkKey): PlatformMetrics => M[k] || (M[k] = { verdict: '' })
const touch = (k: PubLinkKey) => { row(k).updatedAt = isoDay(new Date()) }
function pick(k: PubLinkKey, v: PlatformMetrics['verdict']) { const r = row(k); r.verdict = r.verdict === v ? '' : v; touch(k) }
function setNum(k: PubLinkKey, f: keyof PlatformMetrics, e: Event) {
  const raw = (e.target as HTMLInputElement).value.trim(), r = row(k) as unknown as Record<string, unknown>
  const n = Number(raw.replace(/[.\s]/g, '').replace(',', '.'))
  if (!raw || !Number.isFinite(n) || n < 0) delete r[f]
  else r[f] = n
  touch(k)
}
function setText(k: PubLinkKey, f: 'watch' | 'note', e: Event) { row(k)[f] = (e.target as HTMLInputElement).value; touch(k) }
const numVal = (k: PubLinkKey, f: keyof PlatformMetrics) => { const v = M[k]?.[f]; return typeof v === 'number' ? String(v) : '' }
const addSel = ref('')
function add() { if (addSel.value && !added.value.includes(addSel.value)) added.value.push(addSel.value); addSel.value = '' }
const inp = 'w-full bg-darkinput border border-gray-800 rounded-lg px-3 py-2 text-gray-100 focus:outline-none focus:border-brand transition select-text'
const when = (d?: string) => (d ? fmtDate(dayOf(d), { day: 'numeric', month: 'short', year: 'numeric' }) : '')
</script>

<template>
  <p class="text-sm text-gray-400"><b class="text-gray-100">{{ t('How is it doing?') }}</b> {{ t('For each platform, choose Bad, Average or Good and, if you like, copy the numbers from its statistics. They stay only in Hideo.') }}</p>
  <div v-if="platforms.length" class="space-y-4">
    <section v-for="k in platforms" :key="k" class="rounded-2xl border border-gray-800 p-4 space-y-3">
      <div class="flex items-center gap-2">
        <i :class="[social(k)[2], 'text-lg', { 'text-gray-100': !social(k)[3] }]" :style="social(k)[3] ? { color: social(k)[3] } : undefined"></i>
        <span class="font-black text-gray-100 flex-1">{{ social(k)[1] }}</span>
        <span v-if="M[k]?.updatedAt" class="text-[11px] text-gray-500">{{ t('Updated {date}', { date: when(M[k]?.updatedAt) }) }}</span>
      </div>
      <div class="flex flex-wrap gap-1.5">
        <button v-for="[v, label, txt, bg] in VERDICTS" :key="v" type="button" :data-value="v"
          :class="['pill-btn px-4 py-2 rounded-full font-bold text-xs border transition', M[k]?.verdict === v ? [txt, bg] : 'border-transparent bg-darkinput text-gray-400 hover:text-gray-100']" @click="pick(k, v)">{{ t(label) }}</button>
      </div>
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <label v-for="[f, label] in METRIC_NUMBERS" :key="f" class="block"><span class="block text-[10px] font-bold text-gray-500 mb-1 tracking-wider uppercase">{{ t(label) }}</span>
          <input type="text" inputmode="decimal" :value="numVal(k, f)" :class="inp" @change="setNum(k, f, $event)"></label>
        <label class="block"><span class="block text-[10px] font-bold text-gray-500 mb-1 tracking-wider uppercase">{{ t('Average watch time') }}</span>
          <input type="text" :value="M[k]?.watch || ''" :placeholder="t('e.g. 2:15')" :class="inp" @change="setText(k, 'watch', $event)"></label>
      </div>
      <input type="text" :value="M[k]?.note || ''" :placeholder="t('Notes: what worked, what to change next time…')" :class="inp" @change="setText(k, 'note', $event)">
    </section>
  </div>
  <p v-else class="text-sm text-gray-500">{{ t('No platform yet: add the links in the Released tab, or choose one here.') }}</p>
  <div v-if="others.length" class="flex flex-wrap items-center gap-2">
    <select v-model="addSel" class="bg-darkinput border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-100 focus:outline-none focus:border-brand" @change="add">
      <option value="">{{ t('Add a platform…') }}</option>
      <option v-for="s in others" :key="s[0]" :value="s[0]">{{ s[1] }}</option>
    </select>
  </div>
</template>
